import { createContext } from 'react';

import type { GroupSubscriptionData } from '@shared/dream-components';

import type { UseGroupSubscriptionsModalsResult } from '~/routes/subscribe/$subscriberId/_components/v2/GroupSubscriptions/useGroupSubscriptionsModals';

/**
 * Logic layer for the group-subscriptions surface — the Groups tab (data, cards, modals, seat
 * add/remove/invite + failed-payment retry + restore + change-plan). Self-calls
 * `useGroupSubscriptions` (index + bulk mutators), `useGroupSubscriptionsModals`, and
 * `useRestoreSubscription`; owns the mutation-version counter, the seat-addition-pending watcher,
 * the shared translations dict, and the Stripe billing-portal action. Renders no UI; the colocated
 * `GroupSubscriptionsSection` child renders the Groups tab. The General tab's scenario-applied
 * list is owned by `EffectiveGroupSubscriptionsProvider`, which fetches its own read independent
 * of this provider.
 */
export type GroupSubscriptionsContextType = {
  /** Raw group subscriptions — what the Groups tab renders. */
  groupSubscriptions: GroupSubscriptionData[];
  isLoadingGroupSubscriptions: boolean;
  groupSubscriptionsMutationVersion: number;
  translations: Record<string, string>;
  groupBillingPortalAction: string;
  modals: UseGroupSubscriptionsModalsResult;

  onRetryFailedPayment: (groupId: string) => void;
  onRestoreSubscription: (groupId: string) => Promise<void>;
  onViewBilling: () => void;
};

export const GroupSubscriptionsContext = createContext<GroupSubscriptionsContextType | undefined>(undefined);
GroupSubscriptionsContext.displayName = 'GroupSubscriptionsContext';
